/**
 * Student evaluation record — pure schema helpers, no Firebase.
 *
 * An evaluation in flight is its own document. The student is not reassigned.
 * Dates are the source of truth; the stepper is derived. `isOpen` is the only
 * stored status (the query key). There is no six-value `status` field.
 *
 * Initial evaluations ONLY. A triennial happens inside an existing IEP — the
 * student's own case manager runs it and the reeval-date todos track it. The
 * side record exists for the opposite case: a plan in force (e.g. 504) with a
 * different person running an initial eval for a different plan.
 *
 * Uniqueness (two rules — do not collapse them):
 *   1. At most one open eval per student — keeps `app.evalCaseManagerId` honest.
 *   2. At most one open per (studentId, evalType) — SEIS Pending is refresh.
 *
 * See Documentation/specs/STUDENT_EVALUATION_RECORD.md.
 */

export const EVAL_COLLECTION = 'studentEvaluations'

export const EVAL_TYPES = ['IEP', '504']
export const EVAL_OUTCOMES = ['eligible', 'notEligible', 'withdrawn']

export const EVAL_OUTCOME_LABELS = {
  eligible: 'Eligible',
  notEligible: 'Not eligible',
  withdrawn: 'Withdrawn / no consent'
}

export function evalOutcomeLabel(outcome) {
  const key = cell(outcome)
  return EVAL_OUTCOME_LABELS[key] || key || ''
}
export const EVAL_SOURCES = ['manual', 'seis']

/** Date keys in stepper order. `due` is hand-entered — never computed. */
export const EVAL_DATE_KEYS = ['referral', 'planSent', 'consent', 'due', 'meeting', 'closed']

// AP = assessment plan. Stored keys stay `planSent` / `consent` — labels only.
export const EVAL_STEPPER_STEPS = [
  { key: 'referral', label: 'Referred' },
  { key: 'planSent', label: 'AP sent' },
  { key: 'consent', label: 'AP consent received' },
  { key: 'due', label: 'Due date' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'closed', label: 'Closed' }
]

/** Six-value strings the stale HTML mock stored. Rejected. */
export const REJECTED_STORED_STATUSES = [
  'referred',
  'planSent',
  'consentReceived',
  'consent',
  'due',
  'meetingScheduled',
  'meeting',
  'closed'
]

export const OPEN_EVAL_CODES = {
  ONE_OPEN_PER_STUDENT: 'one-open-per-student',
  ONE_OPEN_PER_TYPE: 'one-open-per-type'
}

/**
 * Roles that may CREATE an evaluation record (2026-08-30: chairs, psych,
 * 504 coordinator, admin tier). Case managers of every variant were removed:
 * they WORK evals assigned to them (EV pill), they do not start them —
 * referrals run through the chair / psych / coordinator, who assign the eval
 * case manager at creation. Mirrors firestore.rules `isEvalCreateRole()`.
 * Keep in sync.
 */
export const EVAL_CREATE_ROLES = [
  'admin',
  'district_admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'school_psych'
]

/**
 * Roles that may OWN / work an evaluation record: the creators plus every
 * case-manager variant an eval can be assigned to. Gates the EV scope pill
 * on Student Management — NOT creation.
 */
export const EVAL_WORK_ROLES = [
  ...EVAL_CREATE_ROLES,
  'case_manager',
  'case_manager_full',
  'speech_case_manager'
]

/** Student Management scope pill. Hidden when evaluations are flagged off. */
export const EVAL_PROVIDER_VIEW = 'evaluations'

export function canUseEvalScopePill(role) {
  return EVAL_WORK_ROLES.includes(cell(role))
}

/**
 * Roles that may CREATE a student document (2026-08-29: full-read creators
 * only). Plain and speech case managers can only read their own caseload, so
 * they cannot check whether an SSID already exists — they were the
 * duplicate-student vector. Every role here passes hasFullReadAccess() and
 * can run a school-wide student query to verify an SSID before creating.
 * Mirrors the students create rule in firestore.rules. Keep in sync.
 */
export const STUDENT_CREATE_ROLES = [
  'admin',
  'district_admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all',
  'case_manager_full',
  'school_psych'
]

export function canCreateStudentRole(role) {
  return STUDENT_CREATE_ROLES.includes(cell(role))
}

/** firestore.rules isAnyAdmin() — school-wide eval list + update. */
export const EVAL_ADMIN_ROLES = [
  'admin',
  'district_admin',
  'school_admin',
  'admin_504',
  'sped_chair',
  'sped_chair_all'
]

export function canCreateEvaluationRole(role) {
  return EVAL_CREATE_ROLES.includes(cell(role))
}

export function isEvalAdminRole(role) {
  return EVAL_ADMIN_ROLES.includes(cell(role))
}

/**
 * Delete is the same set as create (2026-08-30): chairs, psych, 504
 * coordinator, admin tier. Not the assigned plain CM — they work the eval,
 * they do not erase the record. `isOwner` is accepted so existing call sites
 * keep compiling; it is not a grant.
 */
export function canDeleteEvaluationRole(role, _opts = {}) {
  return canCreateEvaluationRole(role)
}

/**
 * Archive (hide from the working list). Eval CM, chairs, psych, and the
 * create-role admin set. Dates / isOpen stay the truth — this is not a
 * second close.
 */
export function canArchiveEvaluationRole(role, { isOwner = false } = {}) {
  return isOwner || canCreateEvaluationRole(role)
}

/**
 * Which initial evaluations can be STARTED for this student. Three scenarios:
 *
 *   1. No plan, no planStatus  → either an IEP or a 504 initial.
 *   2. planStatus 'initial'    → the student's own `plan` field already names
 *                                the plan being assessed; the record matches it.
 *   3. 504 in force            → IEP only (the Jepson case). Another 504
 *                                initial makes no sense while one is in force.
 *   4. IEP in force            → nothing. There is no plan above an IEP to
 *                                initially evaluate for, and a triennial stays
 *                                with their own CM + the reeval-date todos.
 *
 * @param {object} student
 * @returns {string[]} subset of EVAL_TYPES; empty means no Start button
 */
export function allowedInitialEvalTypes(student) {
  const plan = cell(student?.app?.studentData?.plan ?? student?.plan)
  const status = cell(student?.app?.studentData?.planStatus ?? student?.planStatus).toLowerCase()
  const planRecognized = EVAL_TYPES.includes(plan)

  if (!planRecognized) return [...EVAL_TYPES]
  if (status === 'initial') return [plan]
  if (plan === '504') return ['IEP']
  return []
}

/**
 * Initial pending: the student's `plan` names what is being assessed but no
 * plan is in force yet (`planStatus: 'initial'`, the `25 (I-1)` mechanism).
 * State-gated UI hangs off this — accommodations / service providers are
 * disabled for EVERY editor (there is no plan to record them against), the
 * students-table cells show "Initial pending" instead of a blank, and the
 * eval card warns that the schedule was hand-entered.
 */
export function isInitialPending(student) {
  const status = cell(student?.app?.studentData?.planStatus ?? student?.planStatus).toLowerCase()
  return status === 'initial'
}

/**
 * Eval-card schedule banner for initial-pending students.
 *
 * Manual is the normal path: eval-only / no-504 kids are usually absent
 * from the Aeries schedule file, so the typed periods are the real
 * schedule. Always say that when periods exist, even if `manualUpdate`
 * was never stamped (older saves). Only a positive `importUpdate` (this
 * SSID was actually in an Aeries import) changes the copy.
 *
 * A 504-in-force student never uses this banner.
 *
 * @param {object} [student]
 * @returns {{ warn: boolean, text: string } | null}
 */
export function evalScheduleStatus(student) {
  if (!isInitialPending(student)) return null
  const sched = student?.app?.schedule || {}
  const periodCount = Object.keys(sched.periods || {}).length
  if (!periodCount) {
    return {
      warn: true,
      text: 'No schedule yet. Add the student’s periods (student form → Schedule) before sending teacher feedback forms or scheduling the meeting — both are built from the schedule.'
    }
  }
  const imported = sched.importUpdate || null
  if (imported?.at || imported?.by) {
    const when = imported.at ? new Date(imported.at).toLocaleDateString() : ''
    return {
      warn: false,
      text: `Schedule: ${periodCount} period${periodCount === 1 ? '' : 's'}${
        when ? `, from Aeries ${when}` : ', from Aeries'
      }. Confirm the teachers are current before sending feedback forms or scheduling the meeting.`
    }
  }
  const manual = sched.manualUpdate || null
  const when = manual?.at ? new Date(manual.at).toLocaleDateString() : ''
  return {
    warn: false,
    text: `Schedule: ${periodCount} period${periodCount === 1 ? '' : 's'}${
      when ? `, entered manually ${when}` : ', entered manually'
    } — this student is usually not in the Aeries schedule file. Confirm the teachers are current before sending feedback forms or scheduling the meeting.`
  }
}

/**
 * EV roster / card chip. `plan: 'IEP'` on an initial-pending student is
 * the plan being assessed, not a plan in force — do not say "IEP in force".
 * @param {object} [student]
 * @returns {string}
 */
export function evalStudentPlanChip(student) {
  if (!student) return ''
  if (isInitialPending(student)) return 'No plan in force'
  const plan = cell(student?.app?.studentData?.plan ?? student?.plan)
  return plan ? `${plan} in force` : ''
}

/**
 * Mirrored eval CM. Teachers can read this; they cannot read
 * studentEvaluations. CONSENT-GATED (split-grant FERPA design, 2026-10-20):
 * the Cloud Function writes it only once the open eval has dates.consent.
 * During the referral window it is empty — the yellow Initial tile stays off
 * the teacher-readable row, and access flows through evalAccess/{studentId}
 * (CM + admin tier + psych only) plus unlabeled staffIds membership.
 */
export function getEvalCaseManagerId(student) {
  return cell(student?.app?.evalCaseManagerId)
}

export function getPlanCaseManagerId(student) {
  return cell(student?.app?.studentData?.caseManagerId ?? student?.caseManagerId)
}

/**
 * Viewer is the open-eval CM and is not the plan CM. Those students belong
 * on the EV list, not the CM caseload pill or the 25.
 */
export function isEvalOnlyAssignment(student, uid) {
  const id = cell(uid)
  if (!id || !student) return false
  return getEvalCaseManagerId(student) === id && getPlanCaseManagerId(student) !== id
}

/** Plan CM of record only — not the eval CM. */
export function isPlanCaseloadStudent(student, uid) {
  const id = cell(uid)
  return !!id && getPlanCaseManagerId(student) === id
}

/**
 * New Evaluation / SEIS-new fillable shell: leave plan caseManagerId empty
 * so a later Aeries 504 pass can fill it. Existing students stay Path A.
 */
export function isFillableEvalShellCreate({ isNewStudent, planStatus, pending } = {}) {
  if (!isNewStudent) return false
  return cell(planStatus).toLowerCase() === 'initial' || !!pending
}

const EVAL_ONLY_EDIT_ROLES = ['case_manager', 'case_manager_full', 'speech_case_manager']

/**
 * CM variant on an eval they work but do not own as plan CM — or any
 * initial-pending student they can open. Narrows the form to schedule + docs.
 */
export function isEvalOnlyEditor(student, user) {
  const role = cell(user?.role)
  if (!EVAL_ONLY_EDIT_ROLES.includes(role)) return false
  const uid = cell(user?.uid)
  if (!uid) return false
  if (isInitialPending(student)) return true
  const evalCm = getEvalCaseManagerId(student)
  const planCm = getPlanCaseManagerId(student)
  return evalCm === uid && planCm !== uid
}

/**
 * Outcome apply branches on student shape, not on a second chair inbox.
 *
 *   shell        — planStatus initial: no plan in force (fillable shell)
 *   planInForce  — a 504 (or other) plan is already live
 */
export function evalOutcomeStudentShape(student) {
  return isInitialPending(student) ? 'shell' : 'planInForce'
}

/**
 * Who may apply eligibility (write plan + CM, then close). Create-roles,
 * the assigned eval CM, or the psych on the record. Not a teacher / para.
 */
export function canApplyEvalEligibilityRole(
  role,
  { isEvalOwner = false, isEvalPsych = false } = {}
) {
  if (canCreateEvaluationRole(role)) return true
  const r = cell(role)
  if (EVAL_ONLY_EDIT_ROLES.includes(r) && isEvalOwner) return true
  return r === 'school_psych' && isEvalPsych
}

/**
 * Who may remove the student with a not-eligible / withdrawn outcome.
 * Create-roles only, and only a shell (no plan in force).
 */
export function canRemoveEvalShellStudentRole(role, student) {
  return canCreateEvaluationRole(role) && isInitialPending(student)
}

/**
 * Student write for eligible. Caller applies `deleteField()` when
 * `clearPlanStatus` is set. Never includes evalCaseManagerId.
 *
 * @returns {{ ok: boolean, errors: object[], patch: object|null }}
 */
export function buildEligibleStudentApply({ plan, caseManagerId } = {}) {
  const errors = []
  const nextPlan = cell(plan)
  const nextCm = cell(caseManagerId)
  if (!isOneOf(nextPlan, EVAL_TYPES)) {
    errors.push(error('plan', 'enum', 'Eligible must assign IEP or 504.'))
  }
  if (!nextCm) {
    errors.push(error('caseManagerId', 'required', 'Eligible needs a case manager.'))
  }
  if (errors.length) return { ok: false, errors, patch: null }
  return {
    ok: true,
    errors: [],
    patch: {
      plan: nextPlan,
      caseManagerId: nextCm,
      clearPlanStatus: true
    }
  }
}

/**
 * Session copy of a student after a successful eligible apply. The Firestore
 * write already deleted planStatus and the Cloud Function drops the eval
 * mirror when the eval closes; the Students page holds its list for the whole
 * sign-in, so the row has to be patched or "IEP · Initial" and the yellow
 * Pending-initial tile stay up until the next login.
 * Does not mutate `student`.
 */
export function eligibleListStudentPatch(student, { plan, caseManagerId } = {}) {
  const current = student && typeof student === 'object' ? student : {}
  const studentData = { ...(current.app?.studentData || {}) }
  const nextPlan = cell(plan)
  const nextCm = cell(caseManagerId)
  if (nextPlan) studentData.plan = nextPlan
  if (nextCm) studentData.caseManagerId = nextCm
  delete studentData.planStatus
  const app = { ...(current.app || {}), studentData }
  delete app.evalCaseManagerId
  delete app.evalMeetingDate
  return { ...current, app }
}

/**
 * Stamp apply on an existing changeRequest (audit on the closed eval).
 */
export function markChangeRequestApplied(changeRequest, { appliedBy, appliedAt } = {}) {
  const base =
    changeRequest && typeof changeRequest === 'object'
      ? changeRequest
      : buildChangeRequest({ requestedBy: cell(appliedBy) })
  return {
    ...base,
    appliedBy: cell(appliedBy),
    appliedAt: cell(appliedAt) || new Date().toISOString(),
    declinedBy: '',
    declineReason: ''
  }
}

/**
 * Roster yellow-tile type. Do not add `app.evalType` — teachers cannot read
 * the eval collection, so derive from the student already on the row:
 *   planStatus initial → student's plan (the plan being assessed)
 *   plan 504 + mirror  → IEP (the Jepson case)
 *   else + mirror      → IEP
 */
/**
 * Whether the yellow Initial tile is on the student row. Consent received
 * makes the evaluation visible to everyone who can see the student. Before
 * that, the record stays on the Initial list only.
 * @param {string} [consent] dates.consent
 * @returns {{ visible: boolean, label: string, detail: string }}
 */
export function evalListVisibility(consent) {
  if (cell(consent)) {
    return {
      visible: true,
      label: 'Visible on student list',
      detail: 'The yellow tile shows on this student’s row.'
    }
  }
  return {
    visible: false,
    label: 'Hidden on student list',
    detail: 'The yellow tile appears after AP consent is saved.'
  }
}

export function evalRosterInitialType(student) {
  if (!getEvalCaseManagerId(student)) return ''
  const plan = cell(student?.app?.studentData?.plan ?? student?.plan)
  if (isInitialPending(student) && EVAL_TYPES.includes(plan)) return plan
  // Mirror set with a plan in force (or unrecognized plan): the only open
  // eval that shape allows is an IEP initial (the Jepson case).
  return 'IEP'
}

export function isEvalArchived(record) {
  return Boolean(cell(record?.archivedAt))
}

/**
 * After fetchOpenForStudent finds nothing visible, should the card claim
 * "someone else has an open eval"?
 *
 * A leftover `app.evalCaseManagerId` is not that signal when it is the
 * current user's uid (they just closed or deleted their own eval; the CF
 * still has to clear the mirror) or when this session just recorded the
 * close. The Jepson case — 504 CM looking at a student whose IEP eval
 * belongs to someone else — still returns true: mirror is a different uid,
 * query is empty, nobody just closed.
 *
 * @param {{
 *   visibleOpenEval?: object|null,
 *   queryHiddenExisting?: boolean,
 *   mirrorEvalCaseManagerId?: string,
 *   currentUid?: string,
 *   justClosed?: boolean
 * }} [args]
 * @returns {boolean}
 */
export function inferHiddenOpenEval({
  visibleOpenEval = null,
  queryHiddenExisting = false,
  mirrorEvalCaseManagerId = '',
  currentUid = '',
  justClosed = false
} = {}) {
  if (visibleOpenEval) return false
  if (justClosed) return false
  if (queryHiddenExisting) return true
  const mirror = cell(mirrorEvalCaseManagerId)
  if (!mirror) return false
  if (currentUid && mirror === currentUid) return false
  return true
}

/** Roles that may start either eval type (super-admin tier). */
export const EVAL_TYPE_SUPER_ROLES = ['admin', 'district_admin', 'school_admin']

/**
 * Which eval types a ROLE may start — the strict split (2026-08-30):
 *
 *   admin_504                          → 504 initials only
 *   sped chair / school psych          → IEP initials only
 *   admin / district_admin / school_admin → either
 *   case managers (all variants)       → none; they work assigned evals
 *
 * A no-plan 504 referral goes through the 504 coordinator, not a sped role.
 * Mirrors firestore.rules `evalTypeAllowedForRole()`. Keep in sync.
 *
 * @param {string} role
 * @returns {string[]} subset of EVAL_TYPES; empty for non-creators
 */
export function allowedEvalTypesForRole(role) {
  const r = cell(role)
  if (EVAL_TYPE_SUPER_ROLES.includes(r)) return [...EVAL_TYPES]
  if (r === 'admin_504') return ['504']
  if (EVAL_CREATE_ROLES.includes(r)) return ['IEP']
  return []
}

/**
 * Student state ∩ role — what the New Evaluation flow may actually offer.
 * @param {object} student
 * @param {string} role
 * @returns {string[]}
 */
export function startableEvalTypes(student, role) {
  const byRole = allowedEvalTypesForRole(role)
  return allowedInitialEvalTypes(student).filter((t) => byRole.includes(t))
}

function cell(value) {
  return String(value ?? '').trim()
}

function hasDate(value) {
  return cell(value) !== ''
}

function isOneOf(value, allowed) {
  return allowed.includes(value)
}

/**
 * Dates decide open vs closed. `isOpen` on the doc must match this.
 * @param {object} [dates]
 * @returns {boolean}
 */
export function deriveIsOpen(dates = {}) {
  return !hasDate(dates?.closed)
}

/**
 * Whether this record occupies the one open slot on the student.
 * Dates are the truth: no `dates.closed` means the slot is taken, even if
 * stored `isOpen` drifted to false. `isOpen` is the Firestore query key and
 * must agree — that is a validate() concern, not a license for a second eval.
 * @param {object} [record]
 * @returns {boolean}
 */
export function isEvalOpen(record) {
  if (!record || typeof record !== 'object') return false
  return deriveIsOpen(record.dates)
}

/**
 * Stepper from which dates are filled. Never a stored status.
 * The timeline follows the latest date on file: nextStep is the step after
 * that date. A blank earlier step (often the referral date, which is not
 * always known) stays blank and does not hold the status.
 *
 * @param {object} [dates]
 * @returns {{
 *   filled: Record<string, boolean>,
 *   lastCompleted: string|null,
 *   nextStep: string|null,
 *   isClosed: boolean,
 *   isOpen: boolean
 * }}
 */
export function deriveEvalStepper(dates = {}) {
  const filled = {}
  for (const key of EVAL_DATE_KEYS) {
    filled[key] = hasDate(dates?.[key])
  }

  if (filled.closed) {
    return {
      filled,
      lastCompleted: 'closed',
      nextStep: null,
      isClosed: true,
      isOpen: false
    }
  }

  let lastCompleted = null
  for (const key of EVAL_DATE_KEYS) {
    if (filled[key]) lastCompleted = key
  }
  const nextIndex = lastCompleted == null ? 0 : EVAL_DATE_KEYS.indexOf(lastCompleted) + 1

  return {
    filled,
    lastCompleted,
    nextStep: EVAL_DATE_KEYS[nextIndex] || null,
    isClosed: false,
    isOpen: true
  }
}

/**
 * Empty dates object for a new record. Due is not prefilled.
 * @returns {Record<string, string>}
 */
export function emptyEvalDates() {
  return {
    referral: '',
    planSent: '',
    consent: '',
    due: '',
    meeting: '',
    closed: ''
  }
}

/**
 * Which initial meeting `dates.meeting` should follow.
 *
 * Same choice as the student meeting summary: soonest upcoming, and on the
 * same day a confirmed meeting wins over a hold. Only `meetingType: 'initial'`
 * counts — a plan review must not become the eval date. When every initial
 * meeting is in the past, the latest one stays so the eval can still say the
 * meeting was held. No initial meeting means not linked; a hand-entered date
 * is left alone until one exists.
 *
 * @param {object[]} meetings
 * @param {string} today `YYYY-MM-DD`
 * @returns {{ linked: boolean, meetingId: string, date: string }}
 */
export function initialMeetingLink(meetings, today) {
  const day = cell(today)
  const initials = (meetings || []).filter((meeting) => {
    if (!meeting || meeting.cancelled) return false
    if (cell(meeting.meetingType) !== 'initial') return false
    return /^\d{4}-\d{2}-\d{2}$/.test(cell(meeting.date))
  })
  if (!initials.length) return { linked: false, meetingId: '', date: '' }

  const rank = (meeting) => (meeting.status === 'confirmed' ? 0 : 1)
  const upcoming = initials
    .filter((meeting) => cell(meeting.date) >= day)
    .sort((a, b) => {
      const byDate = cell(a.date).localeCompare(cell(b.date))
      if (byDate !== 0) return byDate
      return rank(a) - rank(b)
    })
  const chosen =
    upcoming[0] ||
    [...initials].sort((a, b) => {
      const byDate = cell(b.date).localeCompare(cell(a.date))
      if (byDate !== 0) return byDate
      return rank(a) - rank(b)
    })[0]

  return {
    linked: true,
    meetingId: cell(chosen.id),
    date: cell(chosen.date)
  }
}

/**
 * Patch that keeps an open eval's meeting date on the linked initial meeting.
 * Returns null when the record already matches. Does not clear a hand-entered
 * date that was never tied to a meeting.
 *
 * @param {object} record
 * @param {{ linked?: boolean, meetingId?: string, date?: string }} link
 * @returns {{ 'dates.meeting': string, meetingId: string } | null}
 */
export function evalMeetingLinkPatch(record, link) {
  if (!record || !isEvalOpen(record)) return null
  const currentDate = cell(record.dates?.meeting)
  const currentId = cell(record.meetingId)
  if (link?.linked) {
    const meetingId = cell(link.meetingId)
    const date = cell(link.date)
    if (!date) return null
    if (currentDate === date && currentId === meetingId) return null
    return { 'dates.meeting': date, meetingId }
  }
  if (!currentId) return null
  return { 'dates.meeting': '', meetingId: '' }
}

function pickDates(dates = {}) {
  const next = emptyEvalDates()
  for (const key of EVAL_DATE_KEYS) {
    if (dates?.[key] != null) next[key] = cell(dates[key])
  }
  return next
}

/**
 * Client create/update payload. Timestamps stay with the composable
 * (serverTimestamp). Never writes student `plan` / `caseManagerId`.
 *
 * @param {object} input
 * @returns {object}
 */
export function buildEvaluationRecord(input = {}) {
  const dates = pickDates(input.dates)
  const isOpen = input.isOpen == null ? deriveIsOpen(dates) : input.isOpen === true
  return {
    studentId: cell(input.studentId),
    schoolId: cell(input.schoolId),
    evalType: input.evalType,
    caseManagerId: cell(input.caseManagerId),
    psychId: cell(input.psychId),
    psychIds: normalizePsychIds(input),
    isOpen,
    dates,
    meetingId: cell(input.meetingId),
    outcome: cell(input.outcome),
    changeRequest: input.changeRequest || null,
    source: input.source || 'manual',
    seisStatusSeen: cell(input.seisStatusSeen),
    createdBy: cell(input.createdBy)
  }
}

/**
 * `psychIds` always mirrors the full grant list (single pick → one entry,
 * "All school psychs" → every psych at save time). `psychId` stays for
 * legacy records and single-psych display. Never contains blanks or dupes.
 */
function normalizePsychIds(input = {}) {
  const raw = Array.isArray(input.psychIds) ? input.psychIds : []
  const merged = [cell(input.psychId), ...raw.map(cell)].filter(Boolean)
  return [...new Set(merged)]
}

/**
 * Every uid granted psych access on this record — legacy single `psychId`
 * plus the `psychIds` list. Use for access checks and display, never
 * `record.psychId` alone.
 */
export function evalPsychUids(record = {}) {
  const raw = Array.isArray(record?.psychIds) ? record.psychIds : []
  const merged = [cell(record?.psychId), ...raw.map(cell)].filter(Boolean)
  return [...new Set(merged)]
}

/** Dropdown sentinel for "every school psych gets access". */
export const ALL_PSYCHS_OPTION = '__all__'

/**
 * Dialog selection → record fields. "All" snapshots the psychs at save time —
 * a psych hired later is not auto-added (re-save the eval to refresh).
 */
export function psychSelectionFields(selection, psychs = []) {
  const picked = cell(selection)
  if (picked === ALL_PSYCHS_OPTION) {
    return { psychId: '', psychIds: psychs.map((p) => cell(p?.id)).filter(Boolean) }
  }
  return { psychId: picked, psychIds: picked ? [picked] : [] }
}

/** Record → dialog selection. Any multi-grant list edits as "All". */
export function psychSelectionFromRecord(record = {}) {
  const uids = evalPsychUids(record)
  if (uids.length > 1) return ALL_PSYCHS_OPTION
  return uids[0] || ''
}

/**
 * School psych (`psychId`) and licensed educational psych (`psych2Id`) from a
 * SEIS provider match, shaped for an evaluation record. The school psych is
 * the primary when both matched.
 * @param {object|null} providers
 * @returns {{ psychId: string, psychIds: string[] }|null}
 */
export function evalPsychFieldsFromProviderLinks(providers) {
  const school = cell(providers?.psychId)
  const licensed = cell(providers?.psych2Id)
  const psychIds = [...new Set([school, licensed].filter(Boolean))]
  if (!psychIds.length) return null
  return { psychId: school || licensed, psychIds }
}

/**
 * Psych fields to write on a shell that does not have one yet. A psych
 * already chosen on the record is left alone.
 * @param {object|null} record
 * @param {object|null} providers
 * @returns {{ psychId: string, psychIds: string[] }|null}
 */
export function evalPsychFillIfEmpty(record, providers) {
  if (evalPsychUids(record).length > 0) return null
  return evalPsychFieldsFromProviderLinks(providers)
}

/** Ed Code §56344(a): IEP meeting within 60 days of receiving AP consent. */
export const EVAL_DUE_DAYS = 60
/** A school vacation longer than this many schooldays pauses the 60-day clock. */
export const EVAL_DUE_VACATION_SCHOOLDAYS = 5

function isoFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
}

/**
 * Suggested evaluation due date from SpEd law + the imported school calendar.
 *
 * California Ed Code §56344(a): the IEP meeting to review the assessment must
 * be held within 60 days of receiving parental consent to the assessment
 * plan, NOT counting days between regular school sessions/terms or school
 * vacations longer than five schooldays.
 *
 * Calendar walk from the day after consent: every calendar day counts
 * (weekends and short holidays included) except days inside a non-session run
 * — consecutive weekend/no-school days — containing more than five weekdays
 * (winter break, summer). Days outside the imported calendar are assumed in
 * session, same as the testing calendar.
 *
 * Suggestion only: returns null without a consent date or an imported school
 * calendar. Districts must confirm their own timeline.
 *
 * @param {string} consent - AP consent received (YYYY-MM-DD)
 * @param {object} schoolCalendar - per-school calendar ({ days: { iso: { inSession } } })
 * @returns {{ date: string, excluded: { start: string, end: string }[] } | null}
 */
export function suggestedEvalDueDate(consent, schoolCalendar = {}) {
  const start = cell(consent)
  const days = schoolCalendar?.days || {}
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !Object.keys(days).length) return null
  const startMs = Date.parse(`${start}T00:00:00`)
  if (Number.isNaN(startMs)) return null

  const isNonSession = (d) => {
    if (d.getDay() === 0 || d.getDay() === 6) return true
    const rec = days[isoFromDate(d)]
    return !!rec && rec.inSession === false
  }

  const excluded = []
  let counted = 0
  const cursor = new Date(startMs)
  // Safety cap: a year of school vacations past the window is a broken calendar.
  for (let guard = 0; guard < 500; guard++) {
    cursor.setDate(cursor.getDate() + 1)

    if (isNonSession(cursor)) {
      // Scan the whole run to decide whether it pauses the clock.
      const runStart = new Date(cursor)
      let weekdays = 0
      const probe = new Date(cursor)
      while (isNonSession(probe)) {
        if (probe.getDay() !== 0 && probe.getDay() !== 6) weekdays++
        probe.setDate(probe.getDate() + 1)
      }
      const runEnd = new Date(probe)
      runEnd.setDate(runEnd.getDate() - 1)

      if (weekdays > EVAL_DUE_VACATION_SCHOOLDAYS) {
        excluded.push({ start: isoFromDate(runStart), end: isoFromDate(runEnd) })
        cursor.setTime(runEnd.getTime())
        continue
      }
    }

    counted++
    if (counted >= EVAL_DUE_DAYS) {
      return { date: isoFromDate(cursor), excluded }
    }
  }
  return null
}

/** Info-icon copy for the suggested due date. */
export function suggestedEvalDueDateExplanation(consent, suggestion) {
  if (!suggestion) return ''
  const skipped = (suggestion.excluded || []).map((r) => `${r.start} – ${r.end}`).join(', ')
  return (
    `${EVAL_DUE_DAYS} calendar days from AP consent received (${cell(consent)}), ` +
    `per Ed Code 56344(a). School vacations longer than ${EVAL_DUE_VACATION_SCHOOLDAYS} ` +
    `schooldays pause the clock${skipped ? ` (skipped: ${skipped})` : ''}. ` +
    'Suggested date from the imported school calendar — confirm your district timeline.'
  )
}

/**
 * A studentId + isOpen list that permission-denies means an open eval exists
 * that this caller cannot read (Jepson: 504 CM vs someone else's IEP eval).
 * Treat that as the uniqueness block — do not insert.
 *
 * Only use this for queries an eval-admin is allowed to run. A CM/psych
 * studentId-only list is ALWAYS denied by the rules (even when zero docs
 * exist) — that is not a hidden eval, and must not use this helper.
 *
 * @param {{ permissionDenied?: boolean, docs?: object[] }} result
 */
export function interpretOpenEvalQuery({ permissionDenied = false, docs = [] } = {}) {
  if (permissionDenied) {
    return {
      ok: false,
      hiddenExisting: true,
      existing: [],
      code: OPEN_EVAL_CODES.ONE_OPEN_PER_STUDENT
    }
  }
  return { ok: true, hiddenExisting: false, existing: docs }
}

/**
 * List queries this role may run. School-tier must include schoolId.
 * A CM cannot list the whole school — other owners' docs fail the list rule.
 *
 * When `studentId` is set, admins may query that student directly. Everyone
 * else keeps the owner-shaped queries (caseManagerId / createdBy / psychId)
 * and the caller filters to `studentId`. A studentId-only list is denied for
 * those roles even when the result would be empty — that used to look like
 * "already has an open eval" on a brand-new student.
 *
 * @returns {{ filters: { field: string, value: string|boolean }[] }[]}
 */
export function evaluationListPlans({
  role = '',
  uid = '',
  schoolId = null,
  studentId = null,
  openOnly = true
} = {}) {
  const base = []
  if (schoolId) base.push({ field: 'schoolId', value: schoolId })
  if (openOnly) base.push({ field: 'isOpen', value: true })

  if (isEvalAdminRole(role)) {
    if (studentId) return [{ filters: [...base, { field: 'studentId', value: studentId }] }]
    return [{ filters: base }]
  }

  const plans = [
    { filters: [...base, { field: 'caseManagerId', value: uid }] },
    { filters: [...base, { field: 'createdBy', value: uid }] }
  ]
  if (cell(role) === 'school_psych') {
    // Legacy records carry only psychId; multi-psych records carry psychIds.
    plans.push({ filters: [...base, { field: 'psychId', value: uid }] })
    plans.push({
      filters: [...base, { field: 'psychIds', op: 'array-contains', value: uid }]
    })
  }
  return plans
}

function recordId(record) {
  return cell(record?.id || record?.evalId)
}

/**
 * Open evals for one student (any type).
 * @param {object[]} records
 * @param {string} studentId
 * @param {{ ignoreId?: string }} [options]
 */
/**
 * Latest record the caller can see: an open eval if one exists, otherwise
 * the most recently closed. Closed rows stay as history; they do not expire.
 * @param {object[]} [records]
 * @returns {object|null}
 */
export function pickLatestEvaluation(records = []) {
  const list = (records || []).filter(Boolean)
  if (!list.length) return null
  const open = list.filter((record) => isEvalOpen(record))
  if (open.length) return open[0]
  return [...list].sort((a, b) => cell(b?.dates?.closed).localeCompare(cell(a?.dates?.closed)))[0]
}

export function findOpenEvalsForStudent(records = [], studentId, options = {}) {
  const sid = cell(studentId)
  const ignore = cell(options.ignoreId)
  if (!sid) return []
  return (records || []).filter((record) => {
    if (cell(record?.studentId) !== sid) return false
    if (ignore && recordId(record) === ignore) return false
    return isEvalOpen(record)
  })
}

/**
 * The open eval of a given type, if any. Import refresh key.
 */
export function findOpenEvalByType(records = [], studentId, evalType, options = {}) {
  return (
    findOpenEvalsForStudent(records, studentId, options).find(
      (record) => cell(record.evalType) === cell(evalType)
    ) || null
  )
}

/**
 * Can we insert a new open eval? One open per student — type does not matter.
 *
 * @returns {{
 *   ok: boolean,
 *   code?: string,
 *   sameType?: boolean,
 *   existing?: object
 * }}
 */
export function canCreateOpenEval({ studentId, evalType = '', existing = [], ignoreId = '' } = {}) {
  const open = findOpenEvalsForStudent(existing, studentId, { ignoreId })
  if (open.length === 0) return { ok: true }

  const sameType =
    Boolean(evalType) && open.some((record) => cell(record.evalType) === cell(evalType))
  return {
    ok: false,
    code: OPEN_EVAL_CODES.ONE_OPEN_PER_STUDENT,
    sameType,
    existing: open[0]
  }
}

/**
 * What an import should do with SEIS Pending (once the collection exists).
 *
 * create  — no open eval
 * refresh — open eval of the same type (do not insert another)
 * block   — open eval of a different type (Jepson: IEP eval already running)
 *
 * Until the collection ships, callers must keep Pending report-only.
 */
export function resolveEvalWriteIntent({ studentId, evalType, existing = [], ignoreId = '' } = {}) {
  const open = findOpenEvalsForStudent(existing, studentId, { ignoreId })
  if (open.length === 0) {
    return { action: 'create' }
  }
  const same = open.find((record) => cell(record.evalType) === cell(evalType))
  if (same) {
    return { action: 'refresh', record: same, code: OPEN_EVAL_CODES.ONE_OPEN_PER_TYPE }
  }
  return {
    action: 'block',
    record: open[0],
    code: OPEN_EVAL_CODES.ONE_OPEN_PER_STUDENT
  }
}

/**
 * Scalar mirror for meeting rules. Honest only because one open eval exists.
 * @param {object|null} openEval
 * @returns {string}
 */
export function mirroredEvalCaseManagerId(openEval) {
  if (!isEvalOpen(openEval)) return ''
  return cell(openEval.caseManagerId)
}

function error(field, code, message) {
  return { field, code, message }
}

/**
 * Proposed write a chair applies — not just who asked.
 * @returns {object}
 */
export function buildChangeRequest({ requestedBy, requestedAt = '', proposed = {} } = {}) {
  return {
    requestedBy: cell(requestedBy),
    requestedAt: requestedAt || '',
    proposed: {
      plan: cell(proposed.plan),
      caseManagerId: cell(proposed.caseManagerId),
      retire504: Boolean(proposed.retire504)
    },
    appliedBy: '',
    declinedBy: '',
    declineReason: ''
  }
}

/**
 * @param {object} [changeRequest]
 * @returns {{ ok: boolean, errors: object[] }}
 */
export function validateChangeRequest(changeRequest) {
  const errors = []
  if (!changeRequest || typeof changeRequest !== 'object') {
    return { ok: false, errors: [error('changeRequest', 'required', 'changeRequest is required.')] }
  }

  if (!cell(changeRequest.requestedBy)) {
    errors.push(error('requestedBy', 'required', 'requestedBy is required.'))
  }
  if (!changeRequest.proposed || typeof changeRequest.proposed !== 'object') {
    errors.push(
      error('proposed', 'required', 'proposed must carry plan, caseManagerId, and retire504.')
    )
  } else {
    const plan = cell(changeRequest.proposed.plan)
    if (plan && !isOneOf(plan, EVAL_TYPES)) {
      errors.push(error('proposed.plan', 'enum', 'proposed.plan must be IEP, 504, or blank.'))
    }
    if (typeof changeRequest.proposed.retire504 !== 'boolean') {
      errors.push(error('proposed.retire504', 'type', 'proposed.retire504 must be a boolean.'))
    }
  }

  const applied = cell(changeRequest.appliedBy)
  const declined = cell(changeRequest.declinedBy)
  if (applied && declined) {
    errors.push(
      error('changeRequest', 'conflict', 'A request cannot be both applied and declined.')
    )
  }

  return { ok: errors.length === 0, errors }
}

/**
 * Record an outcome without closing. The spec's eligible path stays open
 * here: outcome + changeRequest, chair applies, then the eval closes.
 * Never a student `plan` / `caseManagerId` patch.
 *
 * @param {object} record
 * @param {{ outcome: string, changeRequest?: object }} args
 */
export function recordEvalOutcome(record, { outcome, changeRequest } = {}) {
  const errors = []
  if (!isOneOf(outcome, EVAL_OUTCOMES)) {
    errors.push(error('outcome', 'enum', 'outcome must be eligible, notEligible, or withdrawn.'))
  }
  if (hasDate(record?.dates?.closed)) {
    errors.push(
      error('dates.closed', 'closed', 'Cannot record an outcome on an already-closed evaluation.')
    )
  }

  const nextRequest = changeRequest === undefined ? record?.changeRequest || null : changeRequest
  if (nextRequest) {
    const check = validateChangeRequest(nextRequest)
    if (!check.ok) errors.push(...check.errors)
  }

  if (errors.length) return { ok: false, errors, patch: null }

  return {
    ok: true,
    errors: [],
    patch: {
      outcome,
      ...(nextRequest ? { changeRequest: nextRequest } : {})
    }
  }
}

/**
 * Close the eval document only. Never a student `plan` / `caseManagerId` patch.
 * Outcome may already be on the record (eligible → chair applies → close).
 *
 * @param {object} record
 * @param {{ outcome?: string, closedDate: string, changeRequest?: object }} args
 */
export function closeEvaluationDraft(
  record,
  { outcome, closedDate, changeRequest, archive = false, archivedBy, archivedAt } = {}
) {
  const errors = []
  const nextOutcome = outcome || record?.outcome
  if (!isOneOf(nextOutcome, EVAL_OUTCOMES)) {
    errors.push(error('outcome', 'enum', 'outcome must be eligible, notEligible, or withdrawn.'))
  }
  if (!hasDate(closedDate)) {
    errors.push(error('dates.closed', 'required', 'closedDate is required to close an evaluation.'))
  }

  const nextRequest = changeRequest || record?.changeRequest || null
  if (nextRequest) {
    const check = validateChangeRequest(nextRequest)
    if (!check.ok) errors.push(...check.errors)
  }

  if (archive && !cell(archivedBy)) {
    errors.push(error('archivedBy', 'required', 'archivedBy is required when archive is set.'))
  }

  if (errors.length) return { ok: false, errors, patch: null }

  return {
    ok: true,
    errors: [],
    patch: {
      isOpen: false,
      outcome: nextOutcome,
      dates: { ...(record?.dates || emptyEvalDates()), closed: closedDate },
      ...(nextRequest ? { changeRequest: nextRequest } : {}),
      ...(archive
        ? {
            archivedAt: cell(archivedAt) || new Date().toISOString(),
            archivedBy: cell(archivedBy)
          }
        : {})
    }
  }
}

/**
 * Hide an already-closed eval from the working list. Not a second isOpen.
 */
export function archiveEvaluationDraft(record, { archivedBy, archivedAt } = {}) {
  const errors = []
  if (isEvalOpen(record)) {
    errors.push(error('archivedAt', 'open', 'Cannot archive an open evaluation. Close it first.'))
  }
  if (isEvalArchived(record)) {
    errors.push(error('archivedAt', 'archived', 'This evaluation is already archived.'))
  }
  if (!cell(archivedBy)) {
    errors.push(error('archivedBy', 'required', 'archivedBy is required.'))
  }
  if (errors.length) return { ok: false, errors, patch: null }
  return {
    ok: true,
    errors: [],
    patch: {
      archivedAt: cell(archivedAt) || new Date().toISOString(),
      archivedBy: cell(archivedBy)
    }
  }
}

function validateDates(dates, isOpen) {
  const errors = []
  if (dates == null || typeof dates !== 'object' || Array.isArray(dates)) {
    return [error('dates', 'required', 'dates must be an object.')]
  }
  const derivedOpen = deriveIsOpen(dates)
  if (isOpen === true && !derivedOpen) {
    errors.push(
      error('isOpen', 'disagree', 'isOpen is true but dates.closed is set. Dates are the truth.')
    )
  }
  if (isOpen === false && derivedOpen) {
    errors.push(error('isOpen', 'disagree', 'isOpen is false but dates.closed is empty.'))
  }
  return errors
}

/**
 * Shape check for a studentEvaluations document. Uniqueness needs the
 * already-loaded open rows — this helper does not query Firestore.
 *
 * @param {object} data
 * @param {{ existing?: object[], ignoreId?: string }} [options]
 * @returns {{ ok: boolean, errors: object[] }}
 */
export function validateEvaluationRecord(data, options = {}) {
  const errors = []
  if (!data || typeof data !== 'object') {
    return { ok: false, errors: [error('', 'required', 'Evaluation record is required.')] }
  }

  if (Object.prototype.hasOwnProperty.call(data, 'status')) {
    errors.push(
      error(
        'status',
        'rejected-status',
        'Do not store a status field. Derive the stepper from dates; store isOpen only.'
      )
    )
  }

  if (!cell(data.studentId)) errors.push(error('studentId', 'required', 'studentId is required.'))
  if (!cell(data.schoolId)) errors.push(error('schoolId', 'required', 'schoolId is required.'))
  if (!isOneOf(data.evalType, EVAL_TYPES)) {
    errors.push(error('evalType', 'enum', 'evalType must be IEP or 504.'))
  }
  // Initial-only. A triennial belongs to the student's existing IEP CM and the
  // reeval-date todo system — it never needs a side record. 'initial' is
  // tolerated on legacy rows; anything else is rejected.
  if (Object.prototype.hasOwnProperty.call(data, 'evalKind') && data.evalKind !== 'initial') {
    errors.push(
      error('evalKind', 'rejected-kind', 'Evaluation records are initial-only. Drop evalKind.')
    )
  }
  if (!cell(data.caseManagerId)) {
    errors.push(error('caseManagerId', 'required', 'caseManagerId (eval owner) is required.'))
  }
  if (!cell(data.createdBy)) {
    errors.push(
      error('createdBy', 'required', 'createdBy is required (creator and owner may differ).')
    )
  }
  if (data.source != null && data.source !== '' && !isOneOf(data.source, EVAL_SOURCES)) {
    errors.push(error('source', 'enum', 'source must be manual or seis.'))
  }
  if (typeof data.isOpen !== 'boolean') {
    errors.push(error('isOpen', 'required', 'isOpen must be a boolean (the query key).'))
  }
  if (data.outcome != null && data.outcome !== '' && !isOneOf(data.outcome, EVAL_OUTCOMES)) {
    errors.push(error('outcome', 'enum', 'outcome must be eligible, notEligible, or withdrawn.'))
  }
  if (data.isOpen === false && !cell(data.outcome)) {
    errors.push(error('outcome', 'required', 'A closed evaluation must have an outcome.'))
  }
  if (data.isOpen === true && cell(data.archivedAt)) {
    errors.push(error('archivedAt', 'open', 'Cannot archive an open evaluation.'))
  }

  errors.push(...validateDates(data.dates, data.isOpen))

  if (data.changeRequest) {
    const check = validateChangeRequest(data.changeRequest)
    if (!check.ok) errors.push(...check.errors)
  }

  if (data.isOpen === true && cell(data.studentId)) {
    const gate = canCreateOpenEval({
      studentId: data.studentId,
      evalType: data.evalType,
      existing: options.existing || [],
      ignoreId: options.ignoreId || recordId(data)
    })
    if (!gate.ok) {
      errors.push(
        error(
          'studentId',
          gate.code,
          gate.sameType
            ? 'An open evaluation of this type already exists — refresh that row, do not insert another.'
            : 'This student already has an open evaluation. One open eval per student.'
        )
      )
    }
  }

  return { ok: errors.length === 0, errors }
}

/**
 * SEIS Pending policy. The collection shipped and the SEIS import runner
 * opted in (2026-08-29) — Pending is create-or-refresh, implemented in
 * seisImportRun.js applySeisPendingEval. Never write planStatus: initial
 * from Pending.
 *
 * `studentHasPlan` is accepted so callers do not invent a third policy
 * ("already has a plan → write planStatus initial"). It does not change
 * the answer. A plan in force is exactly why this is a side record.
 *
 * @param {{ collectionExists?: boolean, studentHasPlan?: boolean }} [args]
 * @returns {'report-only'|'create-or-refresh'}
 */
export function seisPendingImportPolicy({ collectionExists = false } = {}) {
  if (!collectionExists) return 'report-only'
  return 'create-or-refresh'
}

/**
 * Countdown for a hand-entered due date. Never computes the date.
 * `todayYmd` is YYYY-MM-DD from the caller (do not invent a calendar).
 *
 * @param {string} due
 * @param {string} [todayYmd]
 * @returns {{ hasDue: boolean, days: number|null, overdue: boolean, label: string }}
 */
export function evalDueCountdown(due, todayYmd = '') {
  if (!hasDate(due)) {
    return { hasDue: false, days: null, overdue: false, label: 'No due date yet' }
  }
  if (!hasDate(todayYmd)) {
    return { hasDue: true, days: null, overdue: false, label: `Due ${cell(due)}` }
  }
  const dueMs = Date.parse(`${cell(due)}T00:00:00`)
  const todayMs = Date.parse(`${cell(todayYmd)}T00:00:00`)
  if (Number.isNaN(dueMs) || Number.isNaN(todayMs)) {
    return { hasDue: true, days: null, overdue: false, label: `Due ${cell(due)}` }
  }
  const days = Math.round((dueMs - todayMs) / 86400000)
  if (days < 0) {
    const n = Math.abs(days)
    return { hasDue: true, days, overdue: true, label: `${n} day${n === 1 ? '' : 's'} overdue` }
  }
  if (days === 0) return { hasDue: true, days: 0, overdue: false, label: 'Due today' }
  return { hasDue: true, days, overdue: false, label: `${days} day${days === 1 ? '' : 's'} left` }
}
